import React from "react";
import FHIRSummary from "../../components/FHIRSummary";
import SearchRecordTable from "../NewSearchRecords/SearchRecordTable";
import { useAuthState } from "../../components/AuthProvider";
import handleResponse from "../../utils/handleFetchResponse";
import fetchConfig from "../../utils/fetchConfig";
import fhirBundleParser from "../../utils/fhirBundleParser";
import { useMessageToast } from "../../components/MessageProvider";
import Loader from "../../components/Loader";
import Container from "../../components/Container";
import Flex from "../../components/Flex";
import "./Preview.css";

export default function Preview() {
  const toast = useMessageToast();
  const [previewLoadingState, setPreviewLoadingState] = React.useState(false);
  const [identity, setIdentity] = React.useState(null);
  const [priorAggregate, setPriorAggregate] = React.useState(null);
  const [isFocusDeleted, setIsFocusDeleted] = React.useState(false);
  const [destinationGoldenId, setDestinationGoldenId] = React.useState(null);
  const [movedRecords, setMovedRecords] = React.useState([]);
  const [deletedAggregatePrior, setDeletedAggregatePrior] =
    React.useState(null);
  const [primaryRecords, setPrimaryRecords] = React.useState([]);
  const [rqFhirBundleData, setRqFhirBundleData] = React.useState([]);

  // Get goldenId and previewKey from URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlGoldenId = urlParams.get("goldenId");
  const previewKey = urlParams.get("previewKey");

  // Retrieve override_request from localStorage if previewKey exists
  let urlOverrideRequest = null;
  if (previewKey) {
    const storedData = localStorage.getItem(previewKey);
    if (storedData) {
      urlOverrideRequest = storedData;
      // Clean up localStorage after retrieving
      localStorage.removeItem(previewKey);
    }
  }

  const [goldenId, setGoldenId] = React.useState(urlGoldenId);
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const userId = user.username;

  // Fetch override preview
  const recordOverride = React.useCallback(() => {
    setPreviewLoadingState(true);

    // Use override_request from URL params if available, otherwise use default
    let requestPayload;
    if (urlOverrideRequest) {
      try {
        requestPayload = JSON.parse(urlOverrideRequest);
      } catch (e) {
        console.error("Error parsing override_request from URL:", e);
        toast.error("Invalid override request data");
        setPreviewLoadingState(false);
        return;
      }
    }

    if (goldenId !== "") {
      let sourceId = "";
      try {
        if (requestPayload?.patients?.[0]) {
          const firstPatient = JSON.parse(requestPayload.patients[0]);
          sourceId = firstPatient?.meta?.source || "";
        }
      } catch (e) {
        console.error("Error parsing patient for sourceId:", e);
      }

      const url = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patientsource/override`;
      const config = {
        method: "PUT",
        body: JSON.stringify(requestPayload, null, 0),
        credentials: "include",
        headers: {
          clientId: selectedClient.oid,
          "Content-Type": "application/json",
          userId: userId,
          preview: true,
          sourceId: sourceId,
        },
      };

      fetch(...fetchConfig(url, "/overridePreviewMock.json", config))
        .then(handleResponse)
        .then((response) => {
          const rawData = response.data?.map || response.data;

          // Parse aggregatesToDelete
          const aggregatesToDeleteList =
            rawData?.aggregatesToDelete?.myArrayList || [];
          // Match by goldenId suffix so detection works regardless of clientOid
          const focusDeleted = aggregatesToDeleteList.some((item) =>
            item.endsWith(`|${goldenId}`),
          );
          setIsFocusDeleted(focusDeleted);

          // Parse aggregates to find destination and deleted aggregate
          const aggregatesList = rawData?.aggregates?.myArrayList || [];
          let destinationAgg = null;
          let deletedAgg = null;
          for (const agg of aggregatesList) {
            const aggMap = agg.map;
            if (aggMap.golden_id === goldenId && focusDeleted) {
              // This is the focus aggregate (being deleted)
              deletedAgg = aggMap;
            } else {
              destinationAgg = aggMap;
            }
          }

          // If no aggregate is deleted, the focus aggregate is the destination
          if (!focusDeleted && aggregatesList.length > 0) {
            for (const agg of aggregatesList) {
              const aggMap = agg.map;
              if (aggMap.golden_id === goldenId) {
                // This is the focus aggregate
                destinationAgg = aggMap;
              }
            }
          }

          // Parse FHIR Patient from destination aggregate demographics
          let newIdentity = null;
          if (destinationAgg?.demographics) {
            try {
              newIdentity = JSON.parse(destinationAgg.demographics);
            } catch (e) {
              console.error("Error parsing destination demographics:", e);
            }
          }
          setIdentity(newIdentity);
          setDestinationGoldenId(destinationAgg?.golden_id || null);

          // Parse prior demographics from previousDemographicsByAggregateId
          const prevDemos =
            rawData?.previousDemographicsByAggregateId?.myArrayList || [];
          let priorForDestination = null;
          let priorForDeleted = null;
          for (const item of prevDemos) {
            const keys = Object.keys(item.map);
            for (const key of keys) {
              try {
                if (destinationAgg && key === destinationAgg.aggregate_id) {
                  priorForDestination = JSON.parse(item.map[key]);
                }
                // Match by goldenId suffix for the deleted/focus aggregate
                if (key.endsWith(`|${goldenId}`)) {
                  priorForDeleted = JSON.parse(item.map[key]);
                }
              } catch (e) {
                console.error("Error parsing prior demographics:", e);
              }
            }
          }
          setPriorAggregate(
            focusDeleted
              ? priorForDestination
              : priorForDeleted || priorForDestination,
          );
          setDeletedAggregatePrior(priorForDeleted);

          // Extract moved records from the deleted aggregate's old_records
          if (focusDeleted && deletedAgg?.old_records?.myArrayList) {
            setMovedRecords(
              deletedAgg.old_records.myArrayList.map((r) => r.map),
            );
          } else {
            setMovedRecords([]);
          }

          // Parse recordUpdates from response — these are the post-override record states
          const recordUpdatesList = rawData?.recordUpdates?.myArrayList || [];
          const recordUpdatesByRecordId = {};
          const recordUpdateFhirPatients = [];
          for (const ru of recordUpdatesList) {
            const rec = ru.map;
            const recGoldenId = rec.golden_id;
            // Build a unique key: source_system|source_entity_id
            const recKey = `${rec.source_system}|${rec.source_entity_id}`;
            try {
              const fhirPatient =
                typeof rec.demographics === "string"
                  ? JSON.parse(rec.demographics)
                  : rec.demographics;
              recordUpdatesByRecordId[recKey] = {
                fhirPatient,
                goldenId: recGoldenId,
              };
              recordUpdateFhirPatients.push(fhirPatient);
            } catch (e) {
              console.error("Error parsing recordUpdate demographics:", e);
            }
          }

          // Determine the target golden ID for the matcher call
          const targetGoldenId = focusDeleted
            ? destinationAgg?.golden_id
            : goldenId;

          // Call matcher API to get current records, then merge with recordUpdates
          if (
            targetGoldenId &&
            Object.keys(recordUpdatesByRecordId).length > 0
          ) {
            const matcherUrl = `${process.env.REACT_APP_CLIENT_IDENTITY_SERVICE_DOMAIN}/patient/match?include_related_identities=false`;
            const matcherBody = {
              patient: {
                resourceType: "Patient",
                link: [
                  {
                    other: { reference: targetGoldenId },
                    type: "seealso",
                  },
                ],
              },
            };
            const matcherConfig = {
              method: "POST",
              body: JSON.stringify(matcherBody),
              credentials: "include",
              headers: {
                clientId: selectedClient.oid,
                "Content-Type": "application/json",
                userId: userId,
              },
            };

            fetch(
              ...fetchConfig(matcherUrl, "/closeMatches.json", matcherConfig),
            )
              .then(handleResponse)
              .then((matcherResponse) => {
                const matcherData = matcherResponse.data;
                let matcherRecords = [];
                if (
                  matcherData?.resourceType === "Bundle" &&
                  matcherData?.total > 1
                ) {
                  const parsed = fhirBundleParser(matcherData);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    matcherRecords = parsed[0].records || [];
                  }
                }

                // Merge: only include records present in recordUpdates
                const mergedRecords = [];
                const usedKeys = new Set();

                for (const [recKey, ruEntry] of Object.entries(
                  recordUpdatesByRecordId,
                )) {
                  mergedRecords.push(ruEntry.fhirPatient);
                  usedKeys.add(recKey);
                }

                // Include matcher records NOT in recordUpdates (unchanged records still in aggregate)
                for (const mr of matcherRecords) {
                  const mrKey = `${mr.meta?.source}|${mr.id}`;
                  if (!usedKeys.has(mrKey)) {
                    mergedRecords.push(mr);
                  }
                }

                setPrimaryRecords(mergedRecords);
                console.log("Record updates by record ID:", mergedRecords);
                setPreviewLoadingState(false);
              })
              .catch((matcherErr) => {
                console.warn(
                  "Matcher API call failed, using recordUpdates only:",
                  matcherErr,
                );
                // Fallback: use recordUpdates FHIR patients directly
                setPrimaryRecords(recordUpdateFhirPatients);
                setPreviewLoadingState(false);
              });
          } else {
            // No recordUpdates or no target — use empty
            setPrimaryRecords(recordUpdateFhirPatients);
            setPreviewLoadingState(false);
          }
        })
        .catch((e) => {
          console.error("fetchAggregate error; ", e);
          setPreviewLoadingState(false);
          toast.error("Something went wrong while fetching the records");
        });
    }
  }, [goldenId, urlOverrideRequest, selectedClient.oid, userId, toast]);

  React.useEffect(() => {
    if (previewLoadingState) {
      return;
    }
    recordOverride();
  }, [setGoldenId]);

  // Build rqFhirBundleData for SearchRecordTable when primaryRecords change
  React.useEffect(() => {
    if (primaryRecords.length === 0) {
      setRqFhirBundleData([]);
      return;
    }
    const targetId = isFocusDeleted ? destinationGoldenId : goldenId;
    // Ensure each record has score/scoreString fields expected by fhirToDatatable
    const safeRecords = primaryRecords.map((r) => ({
      ...r,
      score: r.score ?? "",
      scoreString: r.scoreString ?? "",
    }));
    setRqFhirBundleData([
      { id: targetId, records: safeRecords, table: "primary" },
    ]);
  }, [primaryRecords, goldenId, destinationGoldenId, isFocusDeleted]);

  return (
    <Container>
      <Flex gutter={3}>
        <Flex.Item span={12}>
          <Loader loading={previewLoadingState}>
            {identity && (
              <>
                <div className="preview-banner">
                  <strong>Preview Mode:</strong> This is a preview of the
                  override changes.
                  <span className="preview-legend-item">
                    <span className="preview-legend-box preview-legend-box-new"></span>
                    New/Changed values
                  </span>
                  <span className="preview-legend-item">
                    <span className="preview-legend-box preview-legend-box-prior"></span>
                    Original values
                  </span>
                </div>

                {isFocusDeleted && (
                  <div className="preview-migration-banner">
                    <div className="preview-migration-header">
                      <span className="preview-migration-icon">⚠️</span>
                      <strong>Aggregate Migration Detected</strong>
                    </div>
                    <p className="preview-migration-detail">
                      EID <strong>{goldenId}</strong> will be{" "}
                      <span className="preview-deleted-badge">deleted</span>.
                      All records are moving to EID{" "}
                      <strong>{destinationGoldenId}</strong>.
                    </p>
                    {movedRecords.length > 0 && (
                      <div className="preview-moved-records">
                        <div className="preview-moved-records-title">
                          Records migrating to EID {destinationGoldenId}:
                        </div>
                        <table className="preview-records-table">
                          <thead>
                            <tr>
                              <th>Source System</th>
                              <th>Record ID</th>
                              <th>Destination EID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movedRecords.map((rec, idx) => (
                              <tr key={idx}>
                                <td>
                                  <code>{rec.source_system}</code>
                                </td>
                                <td>
                                  <code>{rec.source_entity_id}</code>
                                </td>
                                <td>{rec.new_golden_id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {isFocusDeleted && deletedAggregatePrior && (
                  <div className="preview-deleted-aggregate-section">
                    <div className="preview-deleted-aggregate-header">
                      <span className="preview-deleted-badge">Deleted</span>
                      Prior state of EID {goldenId}
                    </div>
                    <FHIRSummary
                      id={goldenId}
                      patientInfo={deletedAggregatePrior}
                      primaryRecords={[]}
                      linkageHistory={[]}
                      handleRecompute={() => {}}
                      canRecompute={false}
                      isInvisible={true}
                      handleRepartition={() => {}}
                      handleMultipleBirth={() => {}}
                      handleAggregateAuxDataRequest={() => {}}
                      isImdm={true}
                    />
                  </div>
                )}

                <div
                  className={
                    isFocusDeleted ? "preview-destination-section" : ""
                  }
                >
                  {isFocusDeleted && (
                    <div className="preview-destination-header">
                      Destination EID {destinationGoldenId} — After Override
                    </div>
                  )}
                  <FHIRSummary
                    id={isFocusDeleted ? destinationGoldenId : goldenId}
                    patientInfo={identity}
                    priorAggregate={priorAggregate}
                    primaryRecords={primaryRecords}
                    linkageHistory={[]}
                    handleRecompute={() => {}}
                    canRecompute={false}
                    isInvisible={true}
                    handleRepartition={() => {}}
                    handleMultipleBirth={() => {}}
                    handleAggregateAuxDataRequest={() => {}}
                    isImdm={true}
                    isPreviewMode={true}
                  />
                </div>

                {/* Focus Identity Records */}
                {rqFhirBundleData.length > 0 && (
                  <SearchRecordTable
                    rq_gid={isFocusDeleted ? destinationGoldenId : goldenId}
                    rq_aggregate={identity}
                    is_review_queue_page={true}
                    rqFhirBundleData={rqFhirBundleData}
                    isPreviewMode={true}
                  />
                )}
              </>
            )}
          </Loader>
        </Flex.Item>
      </Flex>
    </Container>
  );
}
